import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Menu, MenuItem} from "@mui/material";
import React, {useCallback, useState} from "react";
import {themeColors} from "../../styles/theme";
import {useTheme} from "../../context/themeContext";
import {MenuProps} from "@mui/material/Menu";
import {useTranslation} from "react-i18next";


export type ActionMenuProps = {
    id: string;
    loadEntitiesFn: () => Promise<void>;
    deleteEntityFn: (id: string) => Promise<void>
}

export type ActionDialogProps = {
    handleCloseDialog: (event: any) => void;
    handleCloseMenu: (event?: React.MouseEvent | React.KeyboardEvent) => void;
};

export type MenuActionType = {
    label: string;
    dialogTitle?: string;
    dialogContent?: React.FC<ActionDialogProps>;
}

type Props = {
    anchorEl: HTMLElement | null;
    actions?: MenuActionType[];
    onActionClick?: (action: MenuActionType) => void;
} & Pick<MenuProps, 'onClose'>

const ActionMenu = ({anchorEl, actions, onActionClick, onClose}: Props) => {
    const {theme} = useTheme();

    const [currentAction, setCurrentAction] = useState<MenuActionType | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const {t} = useTranslation();

    const backgroundColor = theme === 'light' ? themeColors.white : themeColors.grey4;
    const color = theme === 'light' ? themeColors.navyBlue : themeColors.white;
    const hoverColor = theme === 'light' ? themeColors.lightGrey : themeColors.grey2;

    const {dialogTitle, dialogContent: ActionDialogContent} = currentAction ?? {};

    const handleMenuClose = useCallback((
        event?: React.MouseEvent | React.KeyboardEvent
    ) => {
        const modal = document.querySelector(".MuiDialog-root");
        if (modal && modal.contains(event?.target as Node)) {
            return;
        }

        onClose?.(event as any, 'backdropClick');
    }, [onClose]);

    const handleOpenDialog = useCallback(() => {
        setOpenDialog(true);
    }, []);

    const handleCloseDialog = useCallback((event, reason?: 'backdropClick' | 'escapeKeyDown') => {
        //don't allow to close dialog by click outside or "escape" btn
        if (!reason) {
            setOpenDialog(false);
        }
    }, []);

    const clickAction = useCallback((action: MenuActionType) => {
        setCurrentAction(action);
        if (action.dialogContent) {
            handleOpenDialog();
        }

        onActionClick?.(action);
    }, [onActionClick]);

    return (
        <>
            <Menu
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={onClose}
                disableAutoFocusItem
                disableEnforceFocus
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            color,
                            backgroundColor,
                        },
                    },
                }}
            >
                {
                    actions?.map((action, ind) => (
                        <MenuItem
                            key={`${action.label}_${ind}`}
                            onClick={(event) => clickAction(action)}
                            sx={{
                                color,
                                backgroundColor,
                                "&:hover": {
                                    backgroundColor: hoverColor,
                                },
                            }}
                        >
                            {action.label}
                        </MenuItem>
                    ))
                }
            </Menu>

            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                className="MuiDialog-root"
                slotProps={{
                    paper: {
                        sx: {
                            color,
                            backgroundColor,
                            minWidth: '400px'
                        }
                    }
                }}
            >
                <DialogTitle sx={{}}>
                    {dialogTitle ?? currentAction?.label}
                </DialogTitle>
                <DialogContent>
                    {ActionDialogContent
                        ? <ActionDialogContent handleCloseDialog={handleCloseDialog} handleCloseMenu={handleMenuClose}/>
                        : null
                    }
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>{t("action-menu.dialog-cancel-btn")}</Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default ActionMenu;